import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { invokeEdgeFunction } from '../../lib/supabaseEdge'
import { formatCfgUpdated } from '../../lib/basecampAgentCards'
import { isStageAnalyzerRun } from '../../lib/basecampAgentRunLog'
import { BasecampAgentPipelineOutput } from '../basecamp/BasecampAgentPipelineOutput'

const AGENT_ANALYZER = 'basecamp_data_analyzer'
const AGENT_TASK_CREATOR = 'basecamp_task_creator'
const PIPELINE_OUTLOOK = 'outlook_email_digest'
const STAGE_MAX_ROWS = 50
const SESSION_PREFIX = 'agent-stage-analyzer-'

const modalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.45)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
  boxSizing: 'border-box',
}

const modalPanelStyle = {
  background: '#fff',
  borderRadius: 12,
  maxWidth: 900,
  width: '100%',
  maxHeight: '92vh',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
  border: '1px solid #e2e8f0',
}

const fieldLabel = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: '#475569',
  marginBottom: 6,
}

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  boxSizing: 'border-box',
  fontSize: 14,
}

const primaryBtn = {
  padding: '9px 16px',
  borderRadius: 8,
  border: 'none',
  background: '#2563eb',
  color: '#fff',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
}

const secondaryBtn = {
  padding: '9px 16px',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  background: '#fff',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
}

const SAMPLEABLE_TABLES = ['basecamp_recordings', 'basecamp_recording_comments']

function readSessionAnalyzer(linkId) {
  try {
    const raw = sessionStorage.getItem(`${SESSION_PREFIX}${linkId}`)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function writeSessionAnalyzer(linkId, payload) {
  try {
    sessionStorage.setItem(`${SESSION_PREFIX}${linkId}`, JSON.stringify(payload))
  } catch {
    /* quota */
  }
}

function defaultTableLimits() {
  return {
    basecamp_recordings: STAGE_MAX_ROWS,
    basecamp_recording_comments: STAGE_MAX_ROWS,
  }
}

function formatAnalyzerRunLabel(run) {
  const when = run.finished_at ? formatCfgUpdated(run.finished_at) : '—'
  const ver = run.prompt_version != null ? ` · prompt v${run.prompt_version}` : ''
  return `Run ${run.id.slice(0, 8)}… · ${when}${ver}`
}

export default function AgentRunModal({
  agentKey,
  agentLabel,
  pipeline = null,
  sessionAnalyzerByLinkId,
  onSessionAnalyzerSaved,
  onClose,
}) {
  const isAnalyzer = agentKey === AGENT_ANALYZER
  const isTaskCreator = agentKey === AGENT_TASK_CREATOR
  const isOutlookPipeline = pipeline?.id === PIPELINE_OUTLOOK

  const [links, setLinks] = useState([])
  const [dataSources, setDataSources] = useState([])
  const [linkId, setLinkId] = useState('')
  const [tableLimits, setTableLimits] = useState(defaultTableLimits)
  const [analyzerRuns, setAnalyzerRuns] = useState([])
  const [analyzerInputMode, setAnalyzerInputMode] = useState('db')
  const [analyzerRunId, setAnalyzerRunId] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [result, setResult] = useState(null)

  const sessionForLink = linkId ? sessionAnalyzerByLinkId[linkId] || readSessionAnalyzer(linkId) : null

  const loadLinks = useCallback(async () => {
    const { data, error } = await supabase
      .from('client_basecamp_links')
      .select('id, client_id, basecamp_project_id, basecamp_account_id, clients ( client_name )')
      .order('created_at', { ascending: false })
    if (error) throw error
    setLinks(data || [])
  }, [])

  const loadDataSources = useCallback(async () => {
    const { data, error } = await supabase
      .from('ai_agent_data_sources')
      .select('*')
      .eq('agent_key', agentKey)
      .order('sort_order')
    if (error) throw error
    setDataSources(data || [])
  }, [agentKey])

  const loadAnalyzerRuns = useCallback(async (lid) => {
    if (!lid) {
      setAnalyzerRuns([])
      return
    }
    const { data, error } = await supabase
      .from('basecamp_agent_runs')
      .select('id, finished_at, prompt_version, status, created_at, output_detail')
      .eq('agent_name', AGENT_ANALYZER)
      .eq('status', 'success')
      .eq('client_basecamp_link_id', lid)
      .order('finished_at', { ascending: false })
      .limit(50)
    if (error) throw error
    const stageRuns = (data || []).filter(isStageAnalyzerRun).slice(0, 10)
    setAnalyzerRuns(stageRuns)
  }, [])

  useEffect(() => {
    void (async () => {
      try {
        await Promise.all([loadLinks(), loadDataSources()])
      } catch (e) {
        setErr(e?.message || String(e))
      }
    })()
  }, [loadLinks, loadDataSources])

  useEffect(() => {
    if (!isTaskCreator || !linkId) return
    void loadAnalyzerRuns(linkId).catch((e) => setErr(e?.message || String(e)))
  }, [isTaskCreator, linkId, loadAnalyzerRuns])

  useEffect(() => {
    if (!isTaskCreator || !linkId) return
    if (sessionForLink?.analyzer_output) {
      setAnalyzerInputMode('session')
      setAnalyzerRunId('')
    } else if (analyzerRuns.length > 0) {
      setAnalyzerInputMode('db')
      setAnalyzerRunId(analyzerRuns[0].id)
    } else {
      setAnalyzerInputMode('db')
      setAnalyzerRunId('')
    }
  }, [isTaskCreator, linkId, sessionForLink, analyzerRuns])

  const taskCreatorCanRun = useMemo(() => {
    if (!isTaskCreator || !linkId) return false
    if (analyzerInputMode === 'session' && sessionForLink?.analyzer_output) return true
    if (analyzerInputMode === 'db' && analyzerRunId) return true
    return false
  }, [isTaskCreator, linkId, analyzerInputMode, sessionForLink, analyzerRunId])

  const runStage = async () => {
    setBusy(true)
    setErr('')
    setResult(null)
    try {
      // Outlook pipeline: trigger dry_run of outlook-folder-summarize
      if (isOutlookPipeline) {
        const edgeFn = pipeline?.edge_function || 'outlook-folder-summarize'
        const res = await invokeEdgeFunction(edgeFn, {
          method: 'POST',
          body: JSON.stringify({ dry_run: true }),
        })
        const j = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(j.error || 'Outlook run failed')
        setResult(j)
        return
      }

      // Basecamp pipeline (original path)
      if (!linkId) {
        setErr('Select a client project link.')
        setBusy(false)
        return
      }
      const body = {
        agent_stage_run: true,
        agent_key: agentKey,
        client_basecamp_link_id: linkId,
      }
      if (isAnalyzer) {
        body.table_limits = tableLimits
      } else if (analyzerInputMode === 'session' && sessionForLink?.analyzer_output) {
        body.analyzer_output = sessionForLink.analyzer_output
        if (sessionForLink.run_id) body.analyzer_run_id = sessionForLink.run_id
      } else {
        body.analyzer_run_id = analyzerRunId
      }

      const edgeFn = pipeline?.edge_function || 'basecamp-agents-run'
      const res = await invokeEdgeFunction(edgeFn, {
        method: 'POST',
        body: JSON.stringify(body),
      })
      const j = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(j.error || 'Stage run failed')
      setResult(j)

      if (isAnalyzer && j.analyzer_output) {
        const saved = {
          run_id: j.run_id || null,
          analyzer_output: j.analyzer_output,
          finished_at: new Date().toISOString(),
          link_id: linkId,
        }
        writeSessionAnalyzer(linkId, saved)
        onSessionAnalyzerSaved?.(linkId, saved)
      }
    } catch (e) {
      setErr(e?.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  const selectedLink = links.find((l) => l.id === linkId)
  const linkLabel = selectedLink
    ? `${selectedLink.clients?.client_name || selectedLink.client_id} · project ${selectedLink.basecamp_project_id}`
    : ''

  return (
    <div role="presentation" onClick={onClose} style={modalOverlayStyle}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="agent-run-title"
        onClick={(e) => e.stopPropagation()}
        style={modalPanelStyle}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
          <h2 id="agent-run-title" style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
            Run — {agentLabel}
          </h2>
          <p style={{ margin: '8px 0 0', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
            {isOutlookPipeline
              ? 'Dry run: reads live mailbox folders, runs full LLM pipeline. No email sent and no watermarks advanced.'
              : 'Stage preview only: no watermark update, no email. Uses active prompt from agent config.'}
          </p>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
          {err ? (
            <p style={{ color: '#b91c1c', fontSize: 13, margin: '0 0 12px' }}>{err}</p>
          ) : null}

          {/* Outlook pipeline: no link picker needed */}
          {!isOutlookPipeline ? (
            <>
              <label style={fieldLabel}>Client project link</label>
              <select
                style={{ ...inputStyle, marginBottom: 16 }}
                value={linkId}
                onChange={(e) => {
                  setLinkId(e.target.value)
                  setResult(null)
                }}
              >
                <option value="">Select link…</option>
                {links.map((l) => (
                  <option key={l.id} value={l.id}>
                    {(l.clients?.client_name || l.client_id) ?? 'Client'} — project {l.basecamp_project_id}
                  </option>
                ))}
              </select>
            </>
          ) : (
            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px', lineHeight: 1.5 }}>
              This will run the full Outlook email digest pipeline with{' '}
              <strong>dry_run: true</strong> — no email sent, no watermarks advanced. Results show
              folders processed and extracted bullets.
            </p>
          )}

          {isAnalyzer && !isOutlookPipeline ? (
            <>
              <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px', lineHeight: 1.5 }}>
                Latest rows per table (max {STAGE_MAX_ROWS} each). Watermark ignored.
              </p>
              {SAMPLEABLE_TABLES.map((tableName) => {
                const src = dataSources.find((d) => d.table_name === tableName)
                return (
                  <div key={tableName} style={{ marginBottom: 12 }}>
                    <label style={fieldLabel}>
                      {tableName}
                      {src?.role ? ` (${src.role})` : ''}
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={STAGE_MAX_ROWS}
                      style={{ ...inputStyle, maxWidth: 120 }}
                      value={tableLimits[tableName] ?? STAGE_MAX_ROWS}
                      onChange={(e) => {
                        const n = Math.min(
                          STAGE_MAX_ROWS,
                          Math.max(1, Math.floor(Number(e.target.value) || STAGE_MAX_ROWS))
                        )
                        setTableLimits((prev) => ({ ...prev, [tableName]: n }))
                      }}
                    />
                  </div>
                )
              })}
            </>
          ) : null}

          {isTaskCreator && !isOutlookPipeline ? (
            <>
              <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px', lineHeight: 1.5 }}>
                Input is analyzer output (prior step) — not Basecamp tables. Run the Data Analyzer first
                for this project, or pick a saved analyzer run below.
              </p>
              {!linkId ? (
                <p style={{ fontSize: 13, color: '#b45309' }}>Select a link to choose analyzer input.</p>
              ) : (
                <>
                  {sessionForLink?.analyzer_output ? (
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 8,
                        fontSize: 13,
                        marginBottom: 10,
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        name="analyzerInput"
                        checked={analyzerInputMode === 'session'}
                        onChange={() => setAnalyzerInputMode('session')}
                      />
                      <span>
                        Use analyzer output from this session (just now)
                        {sessionForLink.finished_at
                          ? ` · ${formatCfgUpdated(sessionForLink.finished_at)}`
                          : ''}
                      </span>
                    </label>
                  ) : null}
                  {analyzerRuns.length > 0 ? (
                    <div style={{ marginBottom: 12 }}>
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 8,
                          fontSize: 13,
                          marginBottom: 8,
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="radio"
                          name="analyzerInput"
                          checked={analyzerInputMode === 'db'}
                          onChange={() => {
                            setAnalyzerInputMode('db')
                            if (!analyzerRunId && analyzerRuns[0]) {
                              setAnalyzerRunId(analyzerRuns[0].id)
                            }
                          }}
                        />
                        <span>Use saved analyzer run</span>
                      </label>
                      <select
                        style={{ ...inputStyle, marginLeft: 24 }}
                        value={analyzerRunId}
                        disabled={analyzerInputMode !== 'db'}
                        onChange={(e) => {
                          setAnalyzerInputMode('db')
                          setAnalyzerRunId(e.target.value)
                        }}
                      >
                        <option value="">Select run…</option>
                        {analyzerRuns.map((r) => (
                          <option key={r.id} value={r.id}>
                            {formatAnalyzerRunLabel(r)}
                          </option>
                        ))}
                      </select>
                      <p style={{ margin: '6px 0 0 24px', fontSize: 11, color: '#94a3b8' }}>
                        Last 10 successful stage-preview analyzer runs for this project link.
                      </p>
                    </div>
                  ) : (
                    <p style={{ fontSize: 13, color: '#b45309', margin: '0 0 12px' }}>
                      No successful stage-preview analyzer runs for this link yet. Run the Data Analyzer
                      (Step 1) first.
                    </p>
                  )}
                </>
              )}
            </>
          ) : null}

          {/* Outlook result: folder sections summary */}
          {isOutlookPipeline && result ? (
            <div style={{ marginTop: 8 }}>
              <p style={{ fontSize: 13, color: '#15803d', fontWeight: 600, margin: '0 0 12px' }}>
                {result.message || 'Dry run complete.'}
              </p>
              <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px' }}>
                Folders processed: <strong>{result.folders_processed ?? 0}</strong> ·
                Messages: <strong>{result.messages_processed ?? 0}</strong>
              </p>
              {Array.isArray(result.folder_sections) && result.folder_sections.length > 0 ? (
                <div>
                  {result.folder_sections.map((fs, i) => (
                    <div
                      key={fs.folder_id || i}
                      style={{
                        marginBottom: 16,
                        padding: '12px 14px',
                        border: '1px solid #e2e8f0',
                        borderRadius: 8,
                        background: '#f8fafc',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                          {fs.display_name || fs.folder_id}
                        </span>
                        <span style={{ fontSize: 11, color: '#64748b' }}>
                          {fs.messages_count} msg · {fs.bullets?.length ?? 0} bullets
                        </span>
                        {fs.cap_hit ? (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              background: '#fef3c7',
                              color: '#92400e',
                              padding: '2px 6px',
                              borderRadius: 4,
                            }}
                          >
                            CAP HIT
                          </span>
                        ) : null}
                      </div>
                      {fs.bullets?.slice(0, 5).map((b, bi) => (
                        <p key={bi} style={{ fontSize: 12, color: '#475569', margin: '2px 0', paddingLeft: 8 }}>
                          • {b}
                        </p>
                      ))}
                      {(fs.bullets?.length ?? 0) > 5 ? (
                        <p style={{ fontSize: 11, color: '#94a3b8', margin: '4px 0 0 8px' }}>
                          +{fs.bullets.length - 5} more bullets…
                        </p>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Basecamp result output */}
          {!isOutlookPipeline && result?.data_summary?.length ? (
            <div style={{ marginTop: 20, marginBottom: 16 }}>
              <h4 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                Data used
              </h4>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                    <th style={{ padding: 8, borderBottom: '1px solid #e2e8f0' }}>Source</th>
                    <th style={{ padding: 8, borderBottom: '1px solid #e2e8f0' }}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {result.data_summary.map((row, i) => (
                    <tr key={i}>
                      <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9', color: '#475569' }}>
                        {row.table_name || row.source || '—'}
                      </td>
                      <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9', color: '#334155' }}>
                        {row.rows_fetched != null
                          ? `${row.rows_fetched} rows (limit ${row.limit_applied})`
                          : row.analyzer_run_id
                            ? `Run ${String(row.analyzer_run_id).slice(0, 8)}…${row.finished_at ? ` · ${formatCfgUpdated(row.finished_at)}` : ''}`
                            : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {linkLabel ? (
                <p style={{ fontSize: 12, color: '#94a3b8', margin: '8px 0 0' }}>Link: {linkLabel}</p>
              ) : null}
            </div>
          ) : null}

          {!isOutlookPipeline && result ? (
            <BasecampAgentPipelineOutput
              mode={isAnalyzer ? 'analyzer' : 'task_creator'}
              analyzerText={result.analyzer_output}
              taskCreatorText={result.task_creator_output}
              providerNote={result.provider_note}
              analyzerReportMd={result.analyzer_report_md}
              analyzerFindings={result.analyzer_findings}
              analyzerFollowUpItems={result.analyzer_follow_up_items}
              taskSubject={result.task_subject}
              taskHtmlBody={result.task_html_body}
              analyzerInputExcerpt={result.analyzer_input_excerpt}
            />
          ) : null}
        </div>

        <div
          style={{
            display: 'flex',
            gap: 10,
            padding: '14px 20px',
            borderTop: '1px solid #e2e8f0',
            justifyContent: 'flex-end',
            flexShrink: 0,
            background: '#f8fafc',
          }}
        >
          <button type="button" style={secondaryBtn} onClick={onClose} disabled={busy}>
            Close
          </button>
          {!result ? (
            <button
              type="button"
              style={{
                ...primaryBtn,
                opacity: busy || (!isOutlookPipeline && isTaskCreator && !taskCreatorCanRun) ? 0.55 : 1,
                cursor: busy || (!isOutlookPipeline && isTaskCreator && !taskCreatorCanRun) ? 'not-allowed' : 'pointer',
              }}
              disabled={busy || (!isOutlookPipeline && isTaskCreator && !taskCreatorCanRun)}
              onClick={() => void runStage()}
            >
              {busy ? 'Running…' : isOutlookPipeline ? 'Dry Run' : 'Run'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
